import React, { useEffect, useCallback, useState } from "react";
import { Plus, History, Search as SearchIcon, MessageSquare, Trash } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

interface ConversationSummary {
  conversation_id: string;
  message: string;
  timestamp: string;
  created_at: string;
}

interface ContentChatHeaderControlsProps {
  contentId: string;
  onNewChat: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

export const ContentChatHeaderControls: React.FC<ContentChatHeaderControlsProps> = ({
  contentId,
  onNewChat,
  onSelectConversation,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<ConversationSummary | null>(null);
  const { t } = useLanguage();

  const fetchHistory = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch the first user message per conversation_id for this content
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, message, role, created_at, conversation_id")
        .eq("content_id", contentId)
        .eq("role", "user")
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Group by conversation_id, take the first user message as summary
      const convMap = new Map<string, ConversationSummary>();
      for (const msg of data || []) {
        if (!convMap.has(msg.conversation_id)) {
          convMap.set(msg.conversation_id, {
            conversation_id: msg.conversation_id,
            message: msg.message.length > 60 ? msg.message.substring(0, 60) + "…" : msg.message,
            timestamp: formatDistanceToNow(new Date(msg.created_at), { addSuffix: true }),
            created_at: msg.created_at,
          });
        }
      }

      // Sort by created_at descending
      const sorted = Array.from(convMap.values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setConversations(sorted);
    } catch (error) {
      console.error("Error fetching content chat history:", error);
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Real-time updates for chat history
  useEffect(() => {
    const channel = supabase
      .channel(`content-chat-history-${contentId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_messages', filter: `content_id=eq.${contentId}` },
        () => { fetchHistory(); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [contentId, fetchHistory]);

  const filteredConversations = conversations.filter((c) =>
    c.message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
    <TooltipProvider>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onNewChat}
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:text-primary"
              aria-label="New conversation"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("common.newChat")}</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:text-primary"
                  aria-label="Chat history"
                >
                  <History className="h-4 w-4 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("common.chatHistory")}</p>
            </TooltipContent>
          </Tooltip>

          <DropdownMenuContent align="end" className="z-50 w-80 border border-border bg-card shadow-lg">
            <div className="p-2">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 pl-9"
                />
              </div>
            </div>

            <DropdownMenuSeparator />

            <div className="max-h-[300px] overflow-y-auto">
              {loading ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Loading history...
                </div>
              ) : filteredConversations.length > 0 ? (
                filteredConversations.map((conv) => (
                  <DropdownMenuItem
                    key={conv.conversation_id}
                    className="group/chat-item flex cursor-pointer items-center justify-between gap-2 px-3 py-2"
                    onSelect={(e) => {
                      e.preventDefault();
                      onSelectConversation?.(conv.conversation_id);
                    }}
                  >
                    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                      <span className="w-full truncate text-sm font-medium text-foreground">
                        {conv.message}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {conv.timestamp}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConversationToDelete(conv);
                        setDeleteDialogOpen(true);
                      }}
                      className="flex-shrink-0 rounded-md p-1.5 text-destructive opacity-0 transition-opacity group-hover/chat-item:opacity-100 hover:bg-destructive/10"
                      aria-label="Delete conversation"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center px-4 py-8">
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <MessageSquare className="h-8 w-8 text-primary" />
                  </div>
                  <p className="mb-1 text-sm font-medium text-foreground">
                    {searchQuery ? "No matches found" : "No conversations yet"}
                  </p>
                  <p className="mb-4 text-center text-xs text-muted-foreground">
                    {searchQuery
                      ? "Try adjusting your search"
                      : "Start a conversation to see your history"}
                  </p>
                </div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>

    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete all messages in this conversation. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={async () => {
              if (!conversationToDelete) return;
              try {
                const { error } = await supabase
                  .from("chat_messages")
                  .delete()
                  .eq("conversation_id", conversationToDelete.conversation_id);
                if (error) throw error;
                toast.success("Conversation deleted");
                setConversations((prev) =>
                  prev.filter((c) => c.conversation_id !== conversationToDelete.conversation_id)
                );
                onNewChat();
              } catch (error) {
                console.error("Error deleting conversation:", error);
                toast.error("Failed to delete conversation");
              } finally {
                setDeleteDialogOpen(false);
                setConversationToDelete(null);
              }
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};
